import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    faPlus,
    faEdit,
    faTrash,
    faChevronLeft,
    faChevronRight,
    faDownload,
    faUpload,
    faRobot,
    faInbox
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import GenerateAIQuestions from "./GenerateAIQuestions";
import { PostData, PutData, DeleteData, DownloadData } from '../../../services/FetchData';
import { ProductContext } from '../../../store';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import './AIAssistantProducts.scss';

const AIAssistantQuestions = () => {
    const navigate = useNavigate();
    const { currentWebsiteId } = useContext(ProductContext);
    const [pageLoader, setPageLoader] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [products, setProducts] = useState([]);
    const [isPythonRagTraining, setIsPythonRagTraining] = useState(false);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage] = useState(10);
    const [openDropdowns, setOpenDropdowns] = useState({});
    const [deleteModal, setDeleteModal] = useState(false);
    const [importModal, setImportModal] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showGenerateAI, setShowGenerateAI] = useState(false);

    const getWebsiteId = () => currentWebsiteId || Cookies.get("_website_id");

    const buildListPostData = useCallback((pageIndex) => ({
        websiteId: getWebsiteId(),
        page: pageIndex + 1,
        limit: itemsPerPage,
        searchTerm: appliedSearchTerm || '',
    }), [appliedSearchTerm, itemsPerPage, currentWebsiteId]);

    const applyListResponse = (response) => {
        const pythonMode = Boolean(response.data?.isPythonRagTraining);
        setIsPythonRagTraining(pythonMode);

        if (pythonMode) {
            setProducts(response.data.products || []);
            setQuestions([]);
        } else {
            const questionsData = response.data.questions || [];
            const formattedQuestions = questionsData.map(q => ({
                ...q,
                createdAt: q.createdAt ? new Date(q.createdAt) : new Date(),
                updatedAt: q.updatedAt ? new Date(q.updatedAt) : new Date()
            }));
            setQuestions(formattedQuestions);
            setProducts([]);
        }
        setTotalQuestions(response.data.total || 0);
    };

    const fetchQuestions = useCallback(async (pageIndex = currentPage) => {
        const websiteId = getWebsiteId();

        if (!websiteId) {
            toast.error("Website ID is missing. Please refresh the page.");
            setPageLoader(false);
            return;
        }

        setPageLoader(true);
        try {
            const response = await PostData("ms1", "api/v1/aiassistant/listquestions", buildListPostData(pageIndex));

            if (response !== "Invalid" && response !== undefined) {
                if (response.status === "success") {
                    applyListResponse(response);
                } else {
                    const errorMessage = response.message?.message || response.message || "Failed to fetch questions";
                    toast.error(errorMessage);
                    setQuestions([]);
                    setProducts([]);
                    setTotalQuestions(0);
                }
            } else {
                toast.error("Invalid response from server");
                setQuestions([]);
                setProducts([]);
                setTotalQuestions(0);
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
            toast.error("Failed to fetch questions from API");
            setQuestions([]);
            setProducts([]);
            setTotalQuestions(0);
        } finally {
            setPageLoader(false);
        }
    }, [buildListPostData, currentPage]);

    useEffect(() => {
        fetchQuestions(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, appliedSearchTerm, currentWebsiteId]);

    const toggleDropdown = (questionId) => {
        setOpenDropdowns(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }));
    };

    const handleEditClick = (questionId) => {
        const question = questions.find(q => q.id === questionId);
        if (question) {
            localStorage.removeItem('editingProductQuestions');
            localStorage.setItem('editingQuestion', JSON.stringify(question));
            navigate('/aiassistant/questions/edit');
        }
    };

    const handleEditProductClick = (product) => {
        localStorage.removeItem('editingQuestion');
        localStorage.setItem('editingProductQuestions', JSON.stringify({
            productId: product.productId,
            sku: product.sku,
            questions: product.questions || ['', '', ''],
            isPythonRagTraining: true,
        }));
        navigate('/aiassistant/questions/edit');
    };

    const handleAddQuestion = () => {
        localStorage.removeItem('editingQuestion');
        localStorage.removeItem('editingProductQuestions');
        navigate('/aiassistant/questions/add');
    };

    const handleDeleteClick = (questionId) => {
        setSelectedQuestion(questionId);
        setDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedQuestion) {
            toast.error("Question ID is missing");
            return;
        }

        const websiteId = getWebsiteId();
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        try {
            const questionIdStr = String(selectedQuestion);
            const response = await DeleteData(
                "ms1",
                `api/v1/aiassistant/deletequestion?questionId=${questionIdStr}`,
                { websiteId, questionId: questionIdStr }
            );

            if (response !== "Invalid" && response !== undefined && response.status === "success") {
                toast.success(response.message || "Question deleted successfully");

                const newTotal = totalQuestions - 1;
                const maxPage = Math.ceil(newTotal / itemsPerPage) - 1;
                const adjustedPage = currentPage > maxPage && maxPage >= 0 ? maxPage : currentPage;

                if (adjustedPage !== currentPage) {
                    setCurrentPage(adjustedPage >= 0 ? adjustedPage : 0);
                } else {
                    await fetchQuestions(adjustedPage >= 0 ? adjustedPage : 0);
                }
            } else {
                toast.error(response?.message?.message || response?.message || "Failed to delete question");
            }
        } catch (error) {
            console.error("Error deleting question:", error);
            toast.error("Failed to delete question");
        } finally {
            setDeleteModal(false);
            setSelectedQuestion(null);
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearchTerm('');
        setCurrentPage(0);
        setIsFilterOpen(false);
    };

    const handleApplyFilters = () => {
        setAppliedSearchTerm(searchTerm.trim());
        setCurrentPage(0);
        setIsFilterOpen(false);
    };

    const openFilterSidebar = () => {
        if (!isFilterOpen) {
            setSearchTerm(appliedSearchTerm);
        }
        setIsFilterOpen(!isFilterOpen);
    };

    const closeFilterSidebar = () => {
        setSearchTerm(appliedSearchTerm);
        setIsFilterOpen(false);
    };

    const pageCount = Math.ceil(totalQuestions / itemsPerPage);

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
    };

    const handleExportCSV = async () => {
        const websiteId = getWebsiteId();
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        try {
            const exportPayload = {
                websiteId,
                searchTerm: appliedSearchTerm || '',
            };

            const csvContent = await DownloadData(
                'ms1',
                'api/v1/aiassistant/exportallproductquestions',
                exportPayload
            );

            if (typeof csvContent !== 'string') {
                throw new Error('Unexpected response from export');
            }

            const lineCount = csvContent.split('\n').filter((l) => l.trim()).length;
            const rowCount = Math.max(0, lineCount - 1);
            const filename = isPythonRagTraining
                ? `website_product_questions_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`
                : `product_questions_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            const label = isPythonRagTraining ? 'product(s)' : 'question(s)';
            toast.success(rowCount === 0 ? 'No questions to export' : `Exported ${rowCount} ${label}`);
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error(error.message || 'Failed to export CSV file. Please try again.');
        }
    };

    const handleImportCSV = () => setImportModal(true);

    const handleFileChange = (event) => setSelectedFile(event.target.files[0]);

    const handleImportSubmit = async () => {
        if (!selectedFile) {
            toast.error('Please select a CSV file');
            return;
        }

        const websiteId = getWebsiteId();
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const csv = e.target.result;

                const parseCSVLine = (line) => {
                    const result = [];
                    let current = '';
                    let inQuotes = false;
                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];
                        if (char === '"') {
                            if (inQuotes && line[i + 1] === '"') {
                                current += '"';
                                i++;
                            } else {
                                inQuotes = !inQuotes;
                            }
                        } else if (char === ',' && !inQuotes) {
                            result.push(current.trim());
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                    result.push(current.trim());
                    return result;
                };

                const lines = csv.split('\n').filter(line => line.trim());
                if (lines.length === 0) {
                    toast.error('CSV file is empty');
                    return;
                }

                const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim().replace(/"/g, ''));
                if (!headers.some(h => h === 'question') || !headers.some(h => h === 'sku')) {
                    toast.error('CSV must contain "question" and "sku" columns');
                    return;
                }

                const questionsToImport = [];
                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue;
                    const values = parseCSVLine(lines[i]);
                    const questionIndex = headers.findIndex(h => h === 'question');
                    const skuIndex = headers.findIndex(h => h === 'sku');
                    const answerIndex = headers.findIndex(h => h === 'answer');
                    const isActiveIndex = headers.findIndex(h => h === 'is_active' || h === 'isactive');

                    const row = {
                        question: values[questionIndex] ? values[questionIndex].replace(/^"|"$/g, '') : '',
                        answer: answerIndex >= 0 && values[answerIndex] ? values[answerIndex].replace(/^"|"$/g, '') : '',
                        sku: values[skuIndex] ? values[skuIndex].replace(/^"|"$/g, '') : '',
                        isActive: isActiveIndex >= 0 && values[isActiveIndex]
                            ? (values[isActiveIndex].toLowerCase() === 'true' || values[isActiveIndex] === '1')
                            : true
                    };
                    if (row.question && row.sku) questionsToImport.push(row);
                }

                if (questionsToImport.length === 0) {
                    toast.error('No valid questions found in CSV file');
                    return;
                }

                const response = await PostData("ms1", "api/v1/aiassistant/importquestions", {
                    websiteId,
                    questions: questionsToImport
                });

                if (response !== "Invalid" && response !== undefined && response.status === "success") {
                    const importedCount = response.data?.imported || questionsToImport.length;
                    const errorCount = response.data?.errors?.length || 0;
                    if (errorCount > 0) {
                        toast.warning(`Imported ${importedCount} questions. ${errorCount} errors occurred.`);
                    } else {
                        toast.success(`Successfully imported ${importedCount} questions`);
                    }
                    setImportModal(false);
                    setSelectedFile(null);
                    await fetchQuestions(currentPage);
                } else {
                    toast.error(response?.message?.message || response?.message || "Failed to import questions");
                }
            } catch (error) {
                console.error('Error importing questions:', error);
                toast.error('Error importing questions. Please check the CSV format.');
            }
        };
        reader.readAsText(selectedFile);
    };

    const handleAskAI = () => setShowGenerateAI(true);
    const handleBackToQuestions = () => setShowGenerateAI(false);
    const refreshQuestionsList = () => fetchQuestions(currentPage);

    const handleToggleActive = async (questionId) => {
        const websiteId = getWebsiteId();
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        const question = questions.find(q => q.id === questionId);
        if (!question) return;

        const newActiveState = !question.isActive;
        setQuestions(prev => prev.map(q =>
            q.id === questionId ? { ...q, isActive: newActiveState, updatedAt: new Date() } : q
        ));

        try {
            const questionIdStr = String(questionId);
            const response = await PutData(
                "ms1",
                `api/v1/aiassistant/togglestatus?questionId=${questionIdStr}`,
                { websiteId, questionId: questionIdStr, isActive: newActiveState }
            );

            if (response?.status === "success") {
                toast.success(`Question ${newActiveState ? 'activated' : 'deactivated'} successfully`);
            } else {
                setQuestions(prev => prev.map(q =>
                    q.id === questionId ? { ...q, isActive: !newActiveState } : q
                ));
                toast.error(response?.message?.message || response?.message || "Failed to update question");
            }
        } catch (error) {
            setQuestions(prev => prev.map(q =>
                q.id === questionId ? { ...q, isActive: !newActiveState } : q
            ));
            toast.error("Failed to update question status");
        }
    };

    const getProductRowKey = (product) => `${product.productId}-${product.sku}`;
    const hasRows = isPythonRagTraining ? products.length > 0 : questions.length > 0;

    if (showGenerateAI) {
        return <GenerateAIQuestions onBack={handleBackToQuestions} onRefresh={refreshQuestionsList} />;
    }

    return (
        <React.Fragment>
            <div className="ai-assistant-questions">
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h2 className="mb-1">Product Questions</h2>
                        <p className="text-muted mb-0">Manage your product questions</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button className="filter-btn" color="outline-secondary" onClick={openFilterSidebar}>
                            <i className="fa fa-filter"></i> Filter
                        </Button>
                        <Button color="outline-secondary" className="d-flex align-items-center questions-export-button" onClick={handleExportCSV}>
                            <FontAwesomeIcon icon={faDownload} className="me-2" /> Export CSV
                        </Button>
                        {!isPythonRagTraining && (
                            <>
                                <Button color="outline-secondary" className="d-flex align-items-center questions-import-button" onClick={handleImportCSV}>
                                    <FontAwesomeIcon icon={faUpload} className="me-2" /> Import CSV
                                </Button>
                                <Button color="outline-secondary" className="d-flex align-items-center questions-ask-ai-button" onClick={handleAskAI}>
                                    <FontAwesomeIcon icon={faRobot} className="me-2" /> Ask AI
                                </Button>
                                <Button color="primary" className="d-flex align-items-center conversionBox" onClick={handleAddQuestion}>
                                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Question
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {pageLoader ? (
                    <DetailSkeleton />
                ) : (
                    <div className="table-responsive">
                        <Table className="data-table">
                            <thead>
                                {isPythonRagTraining ? (
                                    <tr>
                                        <th>Product ID</th>
                                        <th>SKU</th>
                                        <th>Question 1</th>
                                        <th>Question 2</th>
                                        <th>Question 3</th>
                                        <th>Actions</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th>ID</th>
                                        <th>Question</th>
                                        <th>Answer</th>
                                        <th>SKU</th>
                                        <th>Is Active</th>
                                        <th>Actions</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {hasRows ? (
                                    isPythonRagTraining ? (
                                        products.map((product) => (
                                            <tr key={getProductRowKey(product)}>
                                                <td><div className="searchItemStatusWrp">{product.productId}</div></td>
                                                <td><div className="searchItemStatusWrp">{product.sku}</div></td>
                                                {[0, 1, 2].map((i) => (
                                                    <td key={i}>
                                                        <div className="searchItemStatusWrp question-text-cell">
                                                            <div className="text-truncate" title={product.questions?.[i] || ''}>
                                                                {product.questions?.[i] || ''}
                                                            </div>
                                                        </div>
                                                    </td>
                                                ))}
                                                <td>
                                                    <Button color="link" className="p-0" onClick={() => handleEditProductClick(product)}>
                                                        <FontAwesomeIcon icon={faEdit} /> Edit
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        questions.map((question) => (
                                            <tr key={question.id}>
                                                <td><div className="searchItemStatusWrp">{question.id}</div></td>
                                                <td>
                                                    <div className="searchItemStatusWrp question-text-cell">
                                                        <div className="text-truncate" title={question.question}>{question.question}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="searchItemStatusWrp answer-text-cell">
                                                        <div className="text-truncate" title={question.answer}>{question.answer}</div>
                                                    </div>
                                                </td>
                                                <td><div className="searchItemStatusWrp">{question.sku}</div></td>
                                                <td>
                                                    <div className="searchItemStatusWrp">
                                                        <div className="form-check form-switch">
                                                            <Input
                                                                className="form-check-input toggle-switch"
                                                                type="checkbox"
                                                                id={`toggle-${question.id}`}
                                                                checked={question.isActive}
                                                                onChange={() => handleToggleActive(question.id)}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Dropdown
                                                        isOpen={openDropdowns[question.id] || false}
                                                        toggle={() => toggleDropdown(question.id)}
                                                        direction="down"
                                                    >
                                                        <DropdownToggle tag="div" className="dropdown-toggle-custom">
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 512" fill="#333" width="18px" height="18px">
                                                                <path d="M64 360a56 56 0 1 0 0 112 56 56 0 1 0 0-112zm0-160a56 56 0 1 0 0 112 56 56 0 1 0 0-112zM120 96A56 56 0 1 0 8 96a56 56 0 1 0 112 0z" />
                                                            </svg>
                                                        </DropdownToggle>
                                                        <DropdownMenu>
                                                            <DropdownItem onClick={() => handleEditClick(question.id)}>
                                                                <span className="dropDownItemWrapper">
                                                                    <FontAwesomeIcon icon={faEdit} /> Edit Question
                                                                </span>
                                                            </DropdownItem>
                                                            <DropdownItem onClick={() => handleDeleteClick(question.id)}>
                                                                <span className="dropDownItemWrapper">
                                                                    <FontAwesomeIcon icon={faTrash} /> Delete Question
                                                                </span>
                                                            </DropdownItem>
                                                        </DropdownMenu>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        ))
                                    )
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="text-center p-5">
                                            <div className="d-flex flex-column align-items-center">
                                                <FontAwesomeIcon icon={faInbox} style={{ fontSize: '48px', color: '#dee2e6' }} />
                                                <p className="text-muted mt-3 mb-0">No Questions Found</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}

                {totalQuestions > 0 && (
                    <ReactPaginate
                        previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
                        nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
                        pageCount={pageCount}
                        onPageChange={handlePageClick}
                        forcePage={currentPage}
                        containerClassName="pagination justify-content-center"
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        previousClassName="page-item"
                        previousLinkClassName="page-link"
                        nextClassName="page-item"
                        nextLinkClassName="page-link"
                        activeClassName="active"
                        breakLabel="..."
                        breakClassName="page-item"
                        breakLinkClassName="page-link"
                    />
                )}

                {isFilterOpen && (
                    <div className="filter-sidebar-overlay" onClick={closeFilterSidebar}>
                        <div className="filter-sidebar" onClick={(e) => e.stopPropagation()}>
                            <div className="filter-sidebar-header">
                                <h5 className="filter-sidebar-title">Filter</h5>
                                <Button className="close-filter-btn" color="link" onClick={closeFilterSidebar}>
                                    <i className="fa fa-times"></i>
                                </Button>
                            </div>
                            <div className="filter-sidebar-content">
                                <div className="filter-section">
                                    <label className="filter-label">Search</label>
                                    <Input
                                        type="text"
                                        placeholder={isPythonRagTraining
                                            ? "Search questions..."
                                            : "Search questions, answers, or SKU..."}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="filter-input"
                                    />
                                </div>
                            </div>
                            <div className="filter-sidebar-footer">
                                <Button className="clear-all-btn" color="secondary" outline onClick={handleClearFilters} style={{ flex: 1, marginRight: '8px' }}>
                                    Clear All
                                </Button>
                                <Button className="apply-filters-btn" color="primary" onClick={handleApplyFilters} style={{ flex: 1 }}>
                                    Apply Filters
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {!isPythonRagTraining && (
                    <>
                        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} className="delete-confirmation-modal">
                            <ModalHeader toggle={() => setDeleteModal(false)} className="delete-modal-header">Confirm Delete</ModalHeader>
                            <ModalBody>Are you sure you want to delete this question? This action cannot be undone.</ModalBody>
                            <ModalFooter className="delete-modal-footer">
                                <Button color="secondary" outline onClick={() => setDeleteModal(false)}>Cancel</Button>
                                <Button color="danger" onClick={confirmDelete}>Delete</Button>
                            </ModalFooter>
                        </Modal>

                        <div className="import-questions-modal-container" style={{ margin: '0 auto' }}>
                            <Modal fullscreen="lg" isOpen={importModal} toggle={() => setImportModal(false)} className="import-questions-modal">
                                <ModalHeader toggle={() => setImportModal(false)}>Import product questions</ModalHeader>
                                <ModalBody style={{ display: 'block' }}>
                                    <p>Upload a CSV file with &apos;question&apos; and &apos;sku&apos; columns. Optionally include &apos;is_active&apos;.</p>
                                    <div className="mb-4 d-flex align-items-center">
                                        <Label for="csvFile" className="form-label fw-bold me-3 mb-0">CSV File</Label>
                                        <Input type="file" id="csvFile" accept=".csv" onChange={handleFileChange} className="form-control" />
                                    </div>
                                    {selectedFile && <small className="text-muted d-block mb-3">Selected: {selectedFile.name}</small>}
                                </ModalBody>
                                <ModalFooter>
                                    <Button color="secondary" outline onClick={() => setImportModal(false)}>Cancel</Button>
                                    <Button color="primary" onClick={handleImportSubmit}>
                                        <FontAwesomeIcon icon={faUpload} className="me-2" /> Import Questions
                                    </Button>
                                </ModalFooter>
                            </Modal>
                        </div>
                    </>
                )}
            </div>
        </React.Fragment>
    );
};

export default AIAssistantQuestions;
